import { useId, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { CycleRef } from '../../../../api/plan-options';
import type { PlanScheme } from '../../../../types/admin';
import { validatePlanSchemeImage } from '../../../../utils/fileValidation';
import { usePlanSchemePreviewUrl } from '../../hooks/usePlanOptions';
import styles from './PlanOptions.module.scss';

type Props = {
  readonly cycle: CycleRef;
  readonly optionId: string;
  readonly label: string;
  readonly schemes: readonly PlanScheme[];
  readonly isVisible: boolean;
  readonly isDirty: boolean;
  readonly isBusy: boolean;
  readonly isDisabled?: boolean;
  readonly onSelectFiles: (files: readonly File[]) => void;
  readonly onDelete: (scheme: PlanScheme) => void;
};

const MAX_SCHEMES_PER_ELEMENT = 10;

function formatBytes(value: number | null | undefined): string | null {
  if (value == null || value < 0) return null;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

export function SchemeImageControl({
  cycle,
  optionId,
  label,
  schemes,
  isVisible,
  isDirty,
  isBusy,
  isDisabled = false,
  onSelectFiles,
  onDelete,
}: Props) {
  const { t } = useTranslation();
  const inputId = useId();
  const [validationError, setValidationError] = useState<string | null>(null);
  const atLimit = schemes.length >= MAX_SCHEMES_PER_ELEMENT;
  const disabled = isDirty || isBusy || isDisabled || atLimit;

  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = '';
    if (files.length === 0) return;

    if (files.length > MAX_SCHEMES_PER_ELEMENT - schemes.length) {
      setValidationError(t('planOptions.schemeTooMany', { max: MAX_SCHEMES_PER_ELEMENT }));
      return;
    }

    const issue = files.map(validatePlanSchemeImage).find(Boolean);
    if (issue) {
      setValidationError(
        t(issue === 'type' ? 'planOptions.schemeInvalidType' : 'planOptions.schemeInvalidSize'),
      );
      return;
    }

    setValidationError(null);
    onSelectFiles(files);
  }

  return (
    <div className={styles.schemeControl} aria-busy={isBusy} data-testid="scheme-control">
      <div className={styles.schemeControlHeader}>
        <span className={styles.schemeControlTitle}>{t('planOptions.schemeImages')}</span>
        {isDirty && <span className={styles.schemeSaveHint}>{t('planOptions.schemeSaveFirst')}</span>}
      </div>

      {schemes.length > 0 && (
        <div className={styles.schemeGallery}>
          {schemes.map((scheme, index) => (
            <SchemePreviewCard
              key={scheme.id}
              cycle={cycle}
              optionId={optionId}
              label={label}
              scheme={scheme}
              index={index}
              isVisible={isVisible}
              disabled={isDirty || isBusy || isDisabled}
              onDelete={() => {
                setValidationError(null);
                onDelete(scheme);
              }}
            />
          ))}
        </div>
      )}

      <div className={styles.schemeActions}>
        <input
          id={inputId}
          type="file"
          multiple
          className={styles.fileInput}
          accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
          disabled={disabled}
          aria-label={`${t('planOptions.schemeUpload')} — ${label}`}
          onChange={handleFile}
        />
        <label
          htmlFor={inputId}
          className={`${styles.uploadLabel} ${disabled ? styles.uploadLabelDisabled : ''}`}
        >
          {isBusy
            ? t('planOptions.schemeUploading')
            : atLimit
              ? t('planOptions.schemeLimitReached', { max: MAX_SCHEMES_PER_ELEMENT })
              : t('planOptions.schemeUpload')}
        </label>
        <span className={styles.schemeRequirements}>{t('planOptions.schemeRequirements')}</span>
      </div>

      {validationError && (
        <p className={styles.schemeError} role="alert">
          {validationError}
        </p>
      )}
    </div>
  );
}

function SchemePreviewCard({
  cycle,
  optionId,
  label,
  scheme,
  index,
  isVisible,
  disabled,
  onDelete,
}: {
  readonly cycle: CycleRef;
  readonly optionId: string;
  readonly label: string;
  readonly scheme: PlanScheme;
  readonly index: number;
  readonly isVisible: boolean;
  readonly disabled: boolean;
  readonly onDelete: () => void;
}) {
  const { t } = useTranslation();
  const preview = usePlanSchemePreviewUrl(cycle, optionId, scheme.id, isVisible);
  const filename = scheme.original_name?.trim() || t('planOptions.schemeUntitled');
  const size = formatBytes(scheme.size_bytes);

  return (
    <article className={styles.schemePreview}>
      <div className={styles.schemePreviewFrame}>
        {preview.url ? (
          <img
            src={preview.url}
            alt={t('planOptions.schemePreviewAltNumbered', { label, number: index + 1 })}
            className={styles.schemePreviewImage}
          />
        ) : preview.isLoading ? (
          <span className={styles.schemePreviewLoading} role="status" aria-live="polite">
            {t('planOptions.schemePreviewLoading')}
          </span>
        ) : (
          <span className={styles.schemePreviewError} role="alert">
            {t('planOptions.schemePreviewError')}
          </span>
        )}
      </div>
      <div className={styles.schemeMeta}>
        <strong title={filename}>{filename}</strong>
        {size && <span>{size}</span>}
      </div>
      <button
        type="button"
        className={styles.schemeDelete}
        disabled={disabled}
        aria-label={`${t('planOptions.schemeDelete')} — ${filename}`}
        onClick={onDelete}
      >
        {t('planOptions.schemeDelete')}
      </button>
    </article>
  );
}
